import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, withCtx as _withCtx, createBlock as _createBlock, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "home" }
const _hoisted_2 = { class: "camera-container" }
const _hoisted_3 = {
  key: 0,
  class: "camera-info"
}
const _hoisted_4 = {
  key: 1,
  class: "video-container"
}
const _hoisted_5 = { class: "camera-controls" }
const _hoisted_6 = {
  key: 2,
  class: "preview-container"
}
const _hoisted_7 = ["src"]
const _hoisted_8 = { class: "preview-controls" }
const _hoisted_9 = {
  key: 3,
  class: "results-container"
}
const _hoisted_10 = ["src"]
const _hoisted_11 = { key: 0 }
const _hoisted_12 = {
  key: 1,
  class: "no-result"
}

import { ref, onMounted, onBeforeUnmount, onUnmounted, computed } from 'vue'
import { Camera, Refresh } from '@element-plus/icons-vue'
import { ElButton, ElIcon, ElTable, ElTableColumn, ElMessage, ElSelect, ElOption } from 'element-plus'
import getDatas from '@/network/index'

// 视频引用

export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const videoRef = ref<HTMLVideoElement | null>(null)
// 拍摄的照片
const capturedImage = ref<string | null>(null)
// 媒体流
const mediaStream = ref<MediaStream | null>(null)
// 上传状态
const uploading = ref(false)
// 识别结果列表
const recognitionResults = ref<Array<{
    id: number,
    image: string,
    result: any,
    timestamp: Date
}>>([])
// 摄像头状态
const cameraStatus = ref<'idle' | 'loading' | 'success' | 'error'>('idle')
// 错误信息
const errorMessage = ref('')
// 可用摄像头列表
const availableCameras = ref<MediaDeviceInfo[]>([])
// 当前使用的摄像头索引
const currentCameraIndex = ref(0)
// 上传方法 ('base64' 或 'image')
const uploadMethod = ref<'base64' | 'image'>('base64')

// 图片压缩配置
const compressConfig = {
    maxWidth: 1280,      // 最大宽度
    maxHeight: 960,      // 最大高度
    quality: 0.7,        // 压缩质量 (0-1)
    maxSizeKB: 500       // 目标最大文件大小 (KB)
}

/**
 * 获取base64字符串的大小（字节）
 */
const getBase64Size = (base64Str: string): number => {
    // 移除data:image/xxx;base64,前缀
    const base64 = base64Str.split(',')[1] || base64Str
    // base64编码后大小约为原始大小的4/3
    return Math.round((base64.length * 3) / 4)
}

/**
 * 压缩图片
 * @param base64Str 原始base64图片字符串
 * @param config 压缩配置
 * @returns 压缩后的base64图片字符串
 */
const compressImage = (base64Str: string, config = compressConfig): Promise<string> => {
    return new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => {
            try {
                // 计算压缩后的尺寸
                let { width, height } = img
                const { maxWidth, maxHeight, quality } = config
                
                // 按比例缩放
                if (width > maxWidth || height > maxHeight) {
                    const ratio = Math.min(maxWidth / width, maxHeight / height)
                    width = Math.round(width * ratio)
                    height = Math.round(height * ratio)
                }
                
                // 创建canvas进行压缩
                const canvas = document.createElement('canvas')
                canvas.width = width
                canvas.height = height
                
                const ctx = canvas.getContext('2d')
                if (!ctx) {
                    reject(new Error('无法创建canvas上下文'))
                    return
                }
                
                // 绘制图片
                ctx.drawImage(img, 0, 0, width, height)
                
                // 转换为base64，使用指定质量
                let compressedBase64 = canvas.toDataURL('image/jpeg', quality)
                
                // 如果还是太大，继续降低质量
                let currentQuality = quality
                const maxSizeBytes = config.maxSizeKB * 1024
                
                while (getBase64Size(compressedBase64) > maxSizeBytes && currentQuality > 0.1) {
                    currentQuality -= 0.1
                    compressedBase64 = canvas.toDataURL('image/jpeg', currentQuality)
                }
                
                console.log(`图片压缩完成: 原尺寸 ${img.width}x${img.height} -> ${width}x${height}, 质量: ${currentQuality.toFixed(1)}, 大小: ${(getBase64Size(compressedBase64) / 1024).toFixed(1)}KB`)
                
                resolve(compressedBase64)
            } catch (err) {
                reject(err)
            }
        }
        img.onerror = () => reject(new Error('图片加载失败'))
        img.src = base64Str
    })
}

// 获取可用摄像头设备
const getAvailableCameras = async () => {
    try {
        if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
            const devices = await navigator.mediaDevices.enumerateDevices();
            availableCameras.value = devices.filter(device => device.kind === 'videoinput');
            console.log('可用摄像头:', availableCameras.value);
        }
    } catch (err) {
        console.error('获取摄像头设备列表失败:', err);
    }
}

// 判断是否为前置摄像头
const isFrontCamera = (device: MediaDeviceInfo): boolean => {
  const frontCameraKeywords = ['front', '前置', 'user', 'selfie', 'face'];
  const label = (device.label || '').toLowerCase();
  return frontCameraKeywords.some(keyword => label.includes(keyword));
};

// 判断是否为后置摄像头
const isBackCamera = (device: MediaDeviceInfo): boolean => {
  const backCameraKeywords = ['back', 'rear', '后置', 'environment'];
  const label = (device.label || '').toLowerCase();
  return backCameraKeywords.some(keyword => label.includes(keyword));
};

// 获取前后摄像头列表
const frontCameras = computed(() => {
  return availableCameras.value.filter(device => isFrontCamera(device));
});

const backCameras = computed(() => {
  return availableCameras.value.filter(device => isBackCamera(device));
});

// 初始化摄像头
const initCamera = async () => {
    try {
        cameraStatus.value = 'loading'
        errorMessage.value = ''
        
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            throw new Error('您的浏览器不支持摄像头功能，请更换现代浏览器')
        }
        
        // 获取可用摄像头列表
        await getAvailableCameras();
        
        // 先尝试使用指定摄像头
        let stream: MediaStream;
        try {
            const constraints: MediaStreamConstraints = { 
                video: {}, 
                audio: false 
            };
            
            // 如果有可用的摄像头列表，使用指定的摄像头
            if (availableCameras.value.length > 0 && availableCameras.value[currentCameraIndex.value]) {
                constraints.video = {
                    deviceId: { exact: availableCameras.value[currentCameraIndex.value].deviceId },
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                };
            } else {
                // 否则使用默认约束
                constraints.video = {
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                };
            }
            
            stream = await navigator.mediaDevices.getUserMedia(constraints);
        } catch (err) {
            console.warn('无法访问指定摄像头，尝试使用默认摄像头:', err)
            // 如果指定摄像头不可用，尝试默认摄像头
            try {
                stream = await navigator.mediaDevices.getUserMedia({ 
                    video: { 
                        width: { ideal: 1280 },
                        height: { ideal: 720 }
                    }, 
                    audio: false 
                })
            } catch (fallbackErr) {
                console.warn('无法访问默认摄像头:', fallbackErr)
                // 如果还是失败，尝试最基本的配置
                stream = await navigator.mediaDevices.getUserMedia({ 
                    video: true, 
                    audio: false 
                })
            }
        }
        
        mediaStream.value = stream
        cameraStatus.value = 'success'
        
        if (videoRef.value) {
            videoRef.value.srcObject = stream
        }
        
    } catch (err) {
        cameraStatus.value = 'error'
        console.error('获取摄像头权限失败:', err)
        
        // 根据错误类型提供不同的提示信息
        if (err instanceof DOMException) {
            switch (err.name) {
                case 'NotFoundError':
                case 'OverconstrainedError':
                    errorMessage.value = '未找到可用的摄像头设备，请检查设备是否连接正常'
                    break
                case 'NotAllowedError':
                case 'SecurityError':
                    errorMessage.value = '摄像头访问被拒绝，请允许浏览器访问摄像头权限'
                    break
                case 'NotReadableError':
                    errorMessage.value = '摄像头正在被其他应用程序占用，请关闭其他使用摄像头的程序'
                    break
                case 'AbortError':
                    errorMessage.value = '摄像头访问被中断'
                    break
                default:
                    errorMessage.value = `摄像头访问失败: ${err.message}`
            }
        } else if (err instanceof Error) {
            errorMessage.value = err.message
        } else {
            errorMessage.value = '未知错误导致摄像头访问失败'
        }
        
        ElMessage.error(errorMessage.value)
    }
}

// 翻转摄像头
const switchCamera = async () => {
    // 如果只有一个摄像头，则不切换
    if (availableCameras.value.length <= 1) return;
    
    // 切换到下一个摄像头
    currentCameraIndex.value = (currentCameraIndex.value + 1) % availableCameras.value.length;
    
    // 重新初始化摄像头
    cleanup()
    await initCamera()
}

// 添加当前摄像头名称的响应式变量
const currentCameraName = computed(() => {
    if (availableCameras.value.length > 0 && availableCameras.value[currentCameraIndex.value]) {
        const currentCamera = availableCameras.value[currentCameraIndex.value];
        // 检查是否为前置摄像头
        if (isFrontCamera(currentCamera)) {
            const frontIndex = frontCameras.value.findIndex(cam => cam.deviceId === currentCamera.deviceId);
            if (frontIndex !== -1) {
                return `前置摄像头 ${frontIndex + 1}`;
            }
        }
        // 检查是否为后置摄像头
        else if (isBackCamera(currentCamera)) {
            const backIndex = backCameras.value.findIndex(cam => cam.deviceId === currentCamera.deviceId);
            if (backIndex !== -1) {
                return `后置摄像头 ${backIndex + 1}`;
            }
        }
        // 如果无法确定类型，则使用默认名称
        return currentCamera.label || `摄像头 ${currentCameraIndex.value + 1}`;
    }
    return '默认摄像头';
});

// 拍照
const capturePhoto = async () => {
    if (!videoRef.value) return
    
    const video = videoRef.value
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    
    const ctx = canvas.getContext('2d')
    if (ctx) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
        const originalBase64 = canvas.toDataURL('image/jpeg', 0.9)
        
        // 压缩图片
        try {
            const originalSize = getBase64Size(originalBase64)
            console.log(`原始图片大小: ${(originalSize / 1024).toFixed(1)}KB`)
            
            // 如果图片大于目标大小，进行压缩
            if (originalSize > compressConfig.maxSizeKB * 1024) {
                capturedImage.value = await compressImage(originalBase64)
                ElMessage.success('图片已自动压缩')
            } else {
                capturedImage.value = originalBase64
            }
        } catch (err) {
            console.error('图片压缩失败，使用原图:', err)
            capturedImage.value = originalBase64
        }
    }
}

// 重新拍照
const retakePhoto = () => {
    capturedImage.value = null
    // 重新初始化摄像头
    initCamera()
}

// 上传照片进行Base64识别
const uploadPhotoAsBase64 = async () => {
    if (!capturedImage.value) return
    
    uploading.value = true

    try {
        // 调用Base64识别API
        const response = await getDatas("home/GetDeviceReadingBase64", capturedImage.value.split(',')[1])
        
        // 添加到结果列表
        recognitionResults.value.unshift({
            id: Date.now(),
            image: capturedImage.value,
            result: response.response || response.data || '无识别结果',
            timestamp: new Date()
        })
        console.log();
        
        ElMessage.success('识别成功')
        capturedImage.value = null
        // 上传成功后重新初始化摄像头
        initCamera()
    } catch (err) {
        console.error('识别失败:', err)
        ElMessage.error('识别失败，请重试')
    } finally {
        uploading.value = false
    }
}

// 上传照片进行图片识别
const uploadPhotoAsImage = async () => {
    if (!capturedImage.value) return
    
    uploading.value = true
    
    try {
        // 将Base64转换为Blob对象
        const blob = await fetch(capturedImage.value).then(res => res.blob());
        const formData = new FormData();
        formData.append('image', blob, 'captured-image.jpg');
 
        // 调用图片识别API
        const response = await getDatas("home/GetDeviceReadingImage", formData);
        console.log(response);
        
        // 添加到结果列表
        recognitionResults.value.unshift({
            id: Date.now(),
            image: capturedImage.value,
            result: response.response || response.data || '无识别结果',
            timestamp: new Date()
        })
        
        ElMessage.success('识别成功')
        capturedImage.value = null
        // 上传成功后重新初始化摄像头
        initCamera()
    } catch (err) {
        console.error('识别失败:', err)
        ElMessage.error('识别失败，请重试')
    } finally {
        uploading.value = false
    }
}

// 根据选择的上传方法调用对应函数
const uploadPhoto = () => {
    if (uploadMethod.value === 'base64') {
        uploadPhotoAsBase64();
    } else {
        uploadPhotoAsImage();
    }
}

// 格式化时间
const formatTime = (date: Date) => {
    return date.toLocaleString('zh-CN')
}

// 清理资源
const cleanup = () => {
    if (mediaStream.value) {
        mediaStream.value.getTracks().forEach(track => track.stop())
        mediaStream.value = null
    }
}

// 重新尝试初始化摄像头
const retryInitCamera = () => {
    cleanup()
    initCamera()
}

// 组件挂载时初始化摄像头
onMounted(() => {
    initCamera()
})

// 组件卸载前清理资源
onBeforeUnmount(() => {
    cleanup()
})

// 页面关闭时也清理资源
onUnmounted(() => {
    cleanup()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[3] || (_cache[3] = _createElementVNode("h2", null, "设备识别", -1)),
      (availableCameras.value.length > 1)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, " 当前摄像头: " + _toDisplayString(currentCameraName.value), 1))
        : _createCommentVNode("", true),
      (!capturedImage.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createElementVNode("video", {
              ref_key: "videoRef",
              ref: videoRef,
              autoplay: "",
              playsinline: "",
              class: "video-stream"
            }, null, 512),
            _createElementVNode("div", _hoisted_5, [
              (availableCameras.value.length > 1)
                ? (_openBlock(), _createBlock(_unref(ElButton), {
                    key: 0,
                    type: "primary",
                    circle: "",
                    size: "small",
                    onClick: switchCamera,
                    class: "switch-camera-btn"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(ElIcon), { size: "16" }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(Refresh))
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true),
              _createVNode(_unref(ElButton), {
                type: "primary",
                circle: "",
                size: "large",
                onClick: capturePhoto,
                class: "capture-btn"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(ElIcon), { size: "24" }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(Camera))
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ])
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_6, [
            _createElementVNode("img", {
              src: capturedImage.value,
              alt: "Captured",
              class: "captured-image"
            }, null, 8, _hoisted_7),
            _createElementVNode("div", _hoisted_8, [
              _createVNode(_unref(ElButton), { onClick: retakePhoto }, {
                default: _withCtx(() => _cache[1] || (_cache[1] = [
                  _createTextVNode("重拍")
                ])),
                _: 1
              }),
              (uploadMethod.value === 'base64')
                ? (_openBlock(), _createBlock(_unref(ElButton), {
                    key: 0,
                    type: "primary",
                    onClick: uploadPhotoAsBase64,
                    loading: uploading.value
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(uploading.value ? '上传中...' : 'Base64识别'), 1)
                    ]),
                    _: 1
                  }, 8, ["loading"]))
                : (_openBlock(), _createBlock(_unref(ElButton), {
                    key: 1,
                    type: "success",
                    onClick: uploadPhotoAsImage,
                    loading: uploading.value
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(uploading.value ? '上传中...' : '图片识别'), 1)
                    ]),
                    _: 1
                  }, 8, ["loading"])),
              _createVNode(_unref(ElSelect), {
                modelValue: uploadMethod.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((uploadMethod).value = $event)),
                placeholder: "选择上传方式",
                size: "small"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(ElOption), {
                    label: "Base64识别",
                    value: "base64"
                  }),
                  _createVNode(_unref(ElOption), {
                    label: "图片识别",
                    value: "image"
                  })
                ]),
                _: 1
              }, 8, ["modelValue"])
            ])
          ])),
      (recognitionResults.value.length > 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
            _cache[2] || (_cache[2] = _createElementVNode("h3", null, "识别记录", -1)),
            _createVNode(_unref(ElTable), {
              data: recognitionResults.value,
              style: { width: '100%' },
              stripe: ""
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(ElTableColumn), {
                  prop: "timestamp",
                  label: "时间",
                  width: "180"
                }, {
                  default: _withCtx((scope) => [
                    _createTextVNode(_toDisplayString(formatTime(scope.row.timestamp)), 1)
                  ]),
                  _: 1
                }),
                _createVNode(_unref(ElTableColumn), {
                  label: "照片",
                  width: "120"
                }, {
                  default: _withCtx((scope) => [
                    _createElementVNode("img", {
                      src: scope.row.image,
                      class: "result-image"
                    }, null, 8, _hoisted_10)
                  ]),
                  _: 1
                }),
                _createVNode(_unref(ElTableColumn), {
                  prop: "data",
                  label: "识别结果"
                }, {
                  default: _withCtx((scope) => [
                    (scope.row.result)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_11, _toDisplayString(scope.row.result), 1))
                      : (_openBlock(), _createElementBlock("div", _hoisted_12, " 无识别结果 "))
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }, 8, ["data"])
          ]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})